/**
 * SocketContext - Connects to Socket.io when authenticated, emits identify.
 */

import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import { selectUser } from '../store/auth.reducer';

const SocketContext = createContext({
  socket: null,
  connected: false,
  subscribe: () => () => {},
});

export const useSocket = () => {
  const context = useContext(SocketContext);
  return context;
};

export const SocketProvider = ({ children }) => {
  const user = useSelector(selectUser);
  const userId = user?.user_id ?? null;
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!userId) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setConnected(false);
      }
      return;
    }

    const socket = io(window.location.origin, { withCredentials: true });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('identify', userId);
      setConnected(true);
    });

    socket.on('disconnect', () => setConnected(false));

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [userId]);

  const value = useMemo(
    () => ({
      get socket() {
        return socketRef.current;
      },
      connected,
      subscribe(event, handler) {
        const socket = socketRef.current;
        if (!socket) return () => {};
        socket.on(event, handler);
        return () => socket.off(event, handler);
      },
    }),
    [connected]
  );

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
